import {useEffect} from 'react';

import {Pagination, TableSearchInput} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {TableSkeleton} from '@/components/Table/TableSkeleton';

import SettingsBusinessCountryTable from './SettingsBusinessCountryTable';
import useBusinessCountryUpdate from './useBusinessCountryUpdate';

/**
 * Settings -> Business Countries.
 *
 * The B2B country catalogue, which had no admin UI at all before this: the
 * collection was only readable through the business dashboard's own
 * `/v2/business/countries`, so nobody outside a logged-in business could see
 * which corridors were open for B2B signup.
 *
 * No status filter bar, unlike the Countries tab: the server route takes no
 * status filter. It does get the same search box, though, so ops can jump
 * straight to a country by name or code without paging through the whole
 * catalogue.
 */
const BusinessCountryList = ({
  setBusinessCountryTotal,
}: {
  setBusinessCountryTotal: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const {
    list,
    isLoading,
    isSearch,
    pageInfo,
    isPageReset,
    setPage,
    searchTerm,
    handleSearch,
  } = useBusinessCountryUpdate();

  useEffect(() => {
    const totals = pageInfo.total
      ? ` (${pageInfo.total.toLocaleString()})`
      : '';
    setBusinessCountryTotal(totals);
  }, [pageInfo, setBusinessCountryTotal]);

  const loadedContent = list?.length ? (
    <SettingsBusinessCountryTable list={list} />
  ) : (
    <div className="flex items-center gap-1 justify-center mt-5">
      <p className="text-gray-800 dark:text-gray-100">
        {searchTerm.trim()
          ? 'No business countries match your search.'
          : 'No business countries found.'}
      </p>
    </div>
  );

  return (
    <main className="pt-5 py-10 overflow-x-auto">
      <div className="w-full flex justify-end mb-3">
        <TableSearchInput
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search by name or code"
        />
      </div>

      {isLoading ? (
        <div className="min-w-full">
          <TableSkeleton />
        </div>
      ) : (
        loadedContent
      )}

      {list?.length ? (
        <Pagination
          isPageReset={isPageReset}
          onPageChange={
            ((params) =>
              setPage(params.page as number)) as PaginationProps['onPageChange']
          }
          total={pageInfo?.total}
          isSearch={isSearch}
        />
      ) : null}
    </main>
  );
};

export default BusinessCountryList;
